import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../config/firebase';
import PreLaunchBanner from '../../components/common/PreLaunchBanner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setSubmitted(true);
    } catch (err) {
      const code = err?.code || '';
      const friendlyMessages = {
        'auth/invalid-email': 'That email address is not valid. Please check and try again.',
        'auth/network-request-failed': 'Network error. Check your internet connection and try again.',
        'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.'
      };
      if (code === 'auth/user-not-found') {
        setSubmitted(true);
        return;
      }
      setError(friendlyMessages[code] || 'Could not send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      {/* LEFT PANEL — form */}
      <div className="flex-1 bg-brand-cream flex items-center justify-center px-5 py-8 sm:px-6 md:px-8 md:py-12 lg:px-12">
        <div className="w-full max-w-md">
          <PreLaunchBanner variant="full" />

          <Link to="/" className="flex items-center gap-3 mt-6 mb-8 md:mb-12">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center font-extrabold text-lg text-brand-dark bg-gradient-to-br from-brand-primary to-brand-primary-light shadow-brand">
              R
            </div>
            <span className="font-serif text-2xl text-brand-dark">Reservo</span>
          </Link>

          {!submitted ? (
            <>
              <h1 className="font-serif text-3xl md:text-4xl text-brand-dark mb-2 tracking-tight">
                Reset your password
              </h1>
              <p className="text-body text-base leading-relaxed mb-6">
                Enter the email address associated with your account. We'll send you a link to set a new password.
              </p>

              <form onSubmit={handleSubmit} className="mb-8">
                <div className="form-group">
                  <label className="form-label">Email address</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="form-error mb-4">
                    {error}
                  </div>
                )}

                <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
                  {loading ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : 'Send reset link'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-serif text-3xl md:text-4xl text-brand-dark mb-2 tracking-tight">
                Check your email
              </h1>
              <p className="text-body text-base leading-relaxed mb-4">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link to it.
                The link expires in 1 hour. Please check your inbox (and spam folder).
              </p>
              <p className="text-body text-base leading-relaxed mb-6">
                Didn't receive an email after a few minutes? You can try again, or contact support if the problem persists.
              </p>
            </>
          )}

          <p className="text-body text-sm text-center mt-4">
            Remembered your password?{' '}
            <Link to="/login" className="text-brand-primary font-semibold">Sign in</Link>
          </p>
        </div>
      </div>

      {/* RIGHT PANEL — marketing cards */}
      <div className="flex-1 bg-brand-dark flex items-center justify-center px-5 py-8 md:px-8 md:py-12 lg:px-12">
        <div className="w-full max-w-md flex flex-col gap-6">
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-card p-6 md:p-7">
            <div className="font-bold text-lg text-brand-primary mb-1">Secure</div>
            <div className="text-white/50 text-sm">Industry-standard password reset</div>
          </div>
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-card p-6 md:p-7">
            <div className="font-bold text-lg text-brand-primary mb-1">Quick</div>
            <div className="text-white/50 text-sm">Reset link arrives within minutes</div>
          </div>
        </div>
      </div>
    </div>
  );
}